import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { styled } from '@mui/material/styles';
import { useCommonTranslation } from '../../../../hooks/useCommonTranslation';
import { ClientLocation } from '../../../../data/models/client-location.model';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import * as clientSlice from '../../../../data/client.slice';
import { useIsSuperReaderUser } from '../../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ClientLocationList';

const classes = {
  container: `${PREFIX}-container`,
  panelTools: `${PREFIX}-panelTools`,
  panelTitle: `${PREFIX}-panelTitle`,
  summary: `${PREFIX}-summary`,
  details: `${PREFIX}-details`,
  label: `${PREFIX}-label`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    gap: theme.spacing(1),
    margin: theme.spacing(2),
  },

  [`& .${classes.panelTools}`]: {
    width: '100%',
  },

  [`& .${classes.panelTitle}`]: {
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },

  [`& .${classes.summary}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },

  [`& .${classes.details}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
  },
}));

interface ClientLocationListProps {
  locations: ClientLocation[];
  onEdit: (location: ClientLocation) => void;
  onDelete: (location: ClientLocation) => void;
}

export default function ClientLocationList({
  locations,
  onEdit,
  onDelete,
}: ClientLocationListProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  const namedLocations = useAppSelector(clientSlice.selectNamedLocations);

  return (
    <StyledBox className={classes.container}>
      {locations.map((location) => {
        const namedLocation = namedLocations.find(
          (nl) => nl.namedLocationUUID === location.namedLocationUUID,
        );

        return (
          <Accordion key={location.clientLocationUUID}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box className={classes.panelTools}>
                <Box className={classes.summary}>
                  <Typography className={classes.panelTitle}>
                    {namedLocation?.name}
                    <Chip label={location.forUseAs} />
                  </Typography>
                  <Box>
                    {!isSuperReaderUser && (
                      <Tooltip title={t('client.editLocation')}>
                        <span>
                          <IconButton
                            data-testid="edit-location"
                            onClick={(evt) => onEdit(location)}
                            color="primary"
                            size="large">
                            <EditOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {!isSuperReaderUser && (
                      <Tooltip title={t('client.deleteLocation')}>
                        <span>
                          <IconButton
                            data-testid="delete-location"
                            onClick={(evt) => onDelete(location)}
                            color="error"
                            size="large">
                            <DeleteForeverIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box className={classes.details}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    {location.description}
                  </Grid>
                  {namedLocation && (
                    <>
                      <Grid item xs={12} className={classes.panelTitle}>
                        {t('namedLocations.reason')}
                      </Grid>
                      <Grid item xs={12}>
                        {namedLocation.reason}
                      </Grid>
                      <Grid item xs={12} className={classes.panelTitle}>
                        {t('namedLocations.address')}
                      </Grid>
                      <Grid item xs={12}>
                        {[
                          namedLocation.addressLine1,
                          namedLocation.addressLine2,
                          namedLocation.city,
                          namedLocation.stateCode,
                          namedLocation.zipCode,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </Grid>
                      <Grid item container spacing={2}>
                        <Grid item xs={12} className={classes.panelTitle}>
                          {t('namedLocations.boundary')}
                        </Grid>
                        <Grid item xs={6}>
                          <Typography className={classes.label}>
                            {t('namedLocations.shape')}
                            {': '}
                            {namedLocation.boundary.shape}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography className={classes.label}>
                            {t('namedLocations.radius')}
                            {': '}
                            {namedLocation.boundary.radius}
                          </Typography>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </StyledBox>
  );
}
